/**
 * Planning Phase 3 P3-7 (plan P3-7 D-321): the rules panel and its determinations, the development schedule, Settings'
 * rule values and the wording of the rule refusals. Spread through intake-en.ts (under `planning.intake.rules` and
 * `planning.errors`), so en.ts does not grow; `rules.evaluated`'s timeline label is intake-en.ts's own (Task 10).
 */
export default {
  rules: {
    title: 'Planning rules',
    advisory:
      'Advisory: the results inform the assessment. A rule that fails or needs an officer needs a determination before the case is tabled.',
    none: 'The rules have not been evaluated for this case.',
    run: 'Evaluate the rules',
    rerun: 'Evaluate again',
    requested: 'The rules were evaluated.',
    failed: 'The last evaluation could not be recorded: {{reason}}',
    meta: 'Revision {{revision}}, evaluated {{date}}',
    packVersion: 'Rule sets of pack version {{version}}',
    valuesChanged: 'The council changed its rule values after this evaluation. Evaluate again to apply them.',
    older: 'This evaluation is of revision {{revision}}; the case is now at revision {{current}}.',
    citation: '{{document}}, p. {{page}}',
    actualRequired: '{{actual}} (required: {{required}})',
    trigger: { staged: 'after the drawing check', on_demand: 'on request' },
    status: {
      pass: 'Meets the rule',
      fail: 'Does not meet the rule',
      needs_officer: 'Needs an officer',
      not_applicable: 'Does not apply',
      not_configured: 'No council value',
    },
    reasons: {
      slots_missing: 'The council has not entered every value this rule reads.',
      schedule_missing: 'No development schedule was declared, or a row of it leaves out a quantity this rule reads.',
      site_missing: 'The revision has no site in the register.',
      flag_missing: 'A case fact this rule reads is not recorded yet.',
      zero_base: 'The base it divides by is zero.',
      lookup_missing: 'The council has no value for a declared development type.',
      lookup_ambiguous: 'More than one development type is declared; the officer decides which value applies.',
      invalid_input: 'A value this rule reads is not a number.',
    },
    reportErrors: {
      register_rows_missing: 'the revision has no register rows',
      superseded: 'a newer revision replaced this one first',
      rules_not_configured: 'no rule values were entered',
      other: 'the evaluation stopped',
    },
    determinations: {
      title: 'Determinations',
      open: '{{number}} awaiting a determination',
      pending: 'Not yet determined',
      recorded: '{{outcome}}, recorded {{date}}',
      note: 'Note: {{note}}',
      record: 'Record',
      change: 'Change',
    },
    determine: {
      title: 'Determination: {{rule}}',
      intro: 'Record your determination of this rule. The results stay advisory.',
      outcome: 'Outcome',
      outcomeRequired: 'Choose an outcome.',
      note: 'Note',
      noteHint: 'Optional. Officers and the council departments see it; the applicant does not.',
      replaces: 'This replaces the determination recorded on {{date}}.',
      confirm: 'Record',
      cancel: 'Cancel',
      done: 'The determination was recorded.',
    },
    outcomes: { complies: 'Complies', acceptable: 'Acceptable', objection: 'Objection' },
    schedule: {
      title: 'Development schedule',
      intro: 'What the applicant declares about the development. The planning rules read it.',
      none: 'No development schedule has been declared.',
      type: 'Development type',
      edit: 'Edit the schedule',
      add: 'Add a row',
      remove: 'Remove',
      removeLabel: 'Remove row {{number}}',
      save: 'Save the schedule',
      cancel: 'Cancel',
      saved: 'The schedule was saved.',
      typeRequired: 'Choose a development type for every row.',
      notNumber: 'Enter a number of 0 or more.',
      notWhole: 'Enter a whole number.',
      tooMany: 'At most {{max}} rows.',
      row: 'Row {{number}}',
    },
    settings: {
      title: 'Planning rules',
      description:
        "The pack ships each rule set's structure and citations. Enter your council's own values; a rule set left empty is not adopted.",
      setsVersion: 'Rule sets of pack version {{version}}',
      none: 'The pack declares no rule sets.',
      adopted: 'Adopted',
      notAdopted: 'Not adopted',
      citationNoPage: '{{document}} (page to be checked)',
      import: 'Import values (JSON)',
      export: 'Export values (JSON)',
      importFailed: 'That file is not a rule values export.',
      imported: 'The values were loaded. Review them, then save.',
      importDropped:
        'Values not loaded: {{count}}. This pack version does not declare them, or they do not fit their field.',
      importVersion: 'This file was exported for another version of the rule sets. Check the values before saving.',
      save: 'Save rule values',
      saving: 'Saving…',
      saved: 'The rule values were saved.',
      blocked: 'Some values cannot be saved. Fix the items marked above.',
      digest: 'Values digest: {{digest}}',
      units: {
        m2: 'square metres',
        percent: 'percent',
        count: 'number',
        ratio: 'ratio',
        per_hectare: 'per hectare',
      },
      bands: {
        upTo: 'Up to',
        open: 'and above',
        per: 'One for every',
        add: 'Add a band',
        remove: 'Remove band {{number}}',
      },
      table: { type: 'Development type', value: 'Value' },
      references: {
        title: 'Development types and your references',
        hint: "Optional: your council's own reference for each type, such as a guideline section. It is kept here with your rule values and exported with them.",
        label: 'Reference for {{type}}',
      },
      problems: {
        notNumber: 'Enter a number of 0 or more.',
        notPercent: 'Enter a share from 0 to 100.',
        bands: 'Bands rise, each with a rate above 0; only the last may have no end.',
        table: 'Enter numbers of 0 or more.',
        reference: 'At most 120 characters.',
        tooLarge: 'At most 1,000,000,000.',
        tooManyBands: 'At most 20 bands.',
      },
    },
  },
  errors: {
    rules_not_configured:
      'No rule values are entered for this council. An administrator enters them in Settings → Planning.',
    rule_report_not_found: 'That rule evaluation is not on this case.',
    rule_report_not_done: 'That rule evaluation did not finish. Reload the page and try again.',
    rule_report_superseded: 'A newer rule evaluation replaced that one. Reload the page and try again.',
    rule_report_changed: 'That rule evaluation changed since the page loaded. Reload the page and try again.',
    rule_not_determinable: 'That rule needs no determination in this evaluation.',
  },
} as const
